// Starts the AI OS shell's content frame (#workspace-resource-frame) as soon as
// the page script runs, without waiting for the shell's LiveView to connect.
//
// The frame has no `src` on purpose (see committee_resource_frame.js): the
// CommitteeResourceFrame hook drives it. But hooks mount only after the shell
// connects, and the shell's connected mount is the slowest step of the page —
// measured on /aios/admin (2026-10-26): the Admin document only started
// ~800 ms after the shell's HTML, and the users table waited for it. Starting
// the same navigation here lets the frame load IN PARALLEL with the shell's
// connection; the hook then recognizes it (`__committeeFramePreload`) and does
// not restart it.
const IGNORED_QUERY_KEYS = ["committee_surface", "committee_origin", "id", "user_id"]

// Normalize a URL for comparison: absolute path + query with the frame-plumbing
// keys stripped. Two URLs that differ only by committee_surface/committee_origin
// (e.g. the frame self-navigated to `?committee_surface=true` while the parent
// recomputed `?committee_surface=true&committee_origin=workspace`) compare EQUAL,
// so the hook never triggers a redundant reload of a frame already in place.
export function normalizePath(raw) {
  try {
    const url = new URL(raw, window.location.origin)
    const params = new URLSearchParams(url.search)
    IGNORED_QUERY_KEYS.forEach(key => params.delete(key))
    // Sort before comparing: the server rebuilds the frame query from a map
    // (`CommitteeNavigation.frame_path/2`), whose key order is not the order
    // the frame's own live-patch produced. Comparing unsorted made two URLs
    // that name the SAME destination look different, and the hook answered
    // with `location.replace` — a real iframe reload that remounted the
    // LiveView and threw away whatever the frame had just navigated to.
    params.sort()
    const query = params.toString()
    return `${url.pathname}${query ? `?${query}` : ""}`
  } catch (_error) {
    return null
  }
}

export function preloadCommitteeFrame(doc = document) {
  const frame = doc.getElementById("workspace-resource-frame")
  const path = frame?.getAttribute("data-frame-path")
  if (!frame || !path || frame.__committeeFramePreload) return

  let target
  try {
    target = new URL(path, window.location.origin)
  } catch (_error) {
    return
  }
  // Same rule as the hook: never drive the content frame off-origin.
  if (target.origin !== window.location.origin) return

  frame.__committeeFramePreload = normalizePath(target.href)
  try {
    // replace(), like the hook: no joint session-history entry.
    frame.contentWindow.location.replace(target.href)
  } catch (_error) {
    frame.__committeeFramePreload = null
  }
}
